import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { getAzAccount } from "../lib/auth.js";
import { runAz } from "../lib/az.js";
import { readFoundryConfig, writeFoundryConfig } from "../lib/foundry-config.js";
import { ensureAppReg, patchRedirectUris } from "../lib/app-reg.js";
import {
  resolveRepoRoot,
  ensureResourceGroup,
  resolveFoundryResourceId,
  runBicepDeployment,
  buildBicepParams,
  resolveAcrPullIdentity,
  resolveAcrResourceId,
  parseFoundryTracingMode,
} from "../lib/deploy.js";
import { LocalCliError } from "../lib/errors.js";
import { runWhatIf, reportWhatIf } from "../lib/whatif.js";
import { classifyWhatIf } from "../lib/whatif-noise.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../lib/output.js";

// The default points at the public GHCR image's moving `latest` tag (published
// by release-image.yml). After the initial deploy, use `m8t platform update` to
// roll onto a pinned vX.Y.Z — it is the supported update path. The BYOC
// private-ACR flow (--image-ref <acr>) is unchanged.
const DEFAULT_IMAGE_REF = "ghcr.io/m8t-run/m8t:latest";

export class DeployCommand extends M8tCommand {
  static paths = [["deploy"]];
  static usage = Command.Usage({
    description: "Deploy (or update) the m8t gateway/webapp stack via Bicep.",
    details:
      "Ensures the Entra app reg (or pass --client-id to reuse an existing one — required if you can't create app regs), writes ~/.m8t-stack/config.yaml, ensures the resource group, and runs deploy/main.bicep. The repo is located via ~/.m8t-stack/repo-root.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", "rg-m8t-stack");
  location = Option.String("--location", "eastus");
  suffix = Option.String("--suffix", "");
  imageRef = Option.String("--image-ref", DEFAULT_IMAGE_REF);
  acrPullIdentity = Option.String("--acrpull-identity");
  acrResourceId = Option.String("--acr-resource-id");
  foundryEndpoint = Option.String("--foundry-endpoint");
  foundryResourceId = Option.String("--foundry-resource-id");
  foundryTracing = Option.String("--foundry-tracing"); // project | account | skip (bicep default: project)
  clientId = Option.String("--client-id");
  whatIf = Option.Boolean("--what-if", false);
  output = Option.String("--output");

  // Referee (E2-F5) — all optional, undefined by default ⇒ the bicep defaults
  // apply (dormant: gatewayCpu=0.25, gatewayMemory=0.5Gi, every referee/exam var
  // empty). Only pass these when explicitly enabling the referee exam stack.
  gatewayCpu = Option.String("--gateway-cpu");
  gatewayMemory = Option.String("--gateway-memory");
  refereeEnabled = Option.String("--referee-enabled");
  refereeBrainRepos = Option.String("--referee-brain-repos");
  refereeFeedRepo = Option.String("--referee-feed-repo");
  refereeInstallationId = Option.String("--referee-installation-id");
  refereeWebhookHmacKvUri = Option.String("--referee-webhook-hmac-kv-uri");
  examKvUri = Option.String("--exam-kv-uri");
  examLaWorkspaceId = Option.String("--exam-la-workspace-id");
  brainEvalDeployment = Option.String("--brain-eval-deployment");
  brainAppLogin = Option.String("--brain-app-login");
  refereeCheckpointDir = Option.String("--referee-checkpoint-dir");
  examApiBase = Option.String("--exam-api-base");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };

    // Prereqs: set subscription FIRST (so the account below reflects the target
    // tenant/sub), then read the account, then resolve the foundry endpoint.
    if (this.subscription) {
      await runAz(["account", "set", "--subscription", this.subscription]);
    }
    const account = await getAzAccount();
    const foundryEndpoint = this.foundryEndpoint ?? (await readFoundryConfig())?.projectEndpoint;
    if (!foundryEndpoint) {
      throw new LocalCliError({
        code: "DEPLOY_NO_FOUNDRY_ENDPOINT",
        message: "No Foundry endpoint provided.",
        hint: "Pass --foundry-endpoint <url> or set projectEndpoint in ~/.m8t-stack/config.yaml (m8t config set / m8t switch).",
      });
    }

    if (this.whatIf) {
      // Strictly read-only: NO app-reg create, NO config write, NO RG create.
      const clientId = this.clientId ?? (await readFoundryConfig())?.clientId;
      if (!clientId) {
        throw new LocalCliError({
          code: "DEPLOY_WHATIF_NO_CLIENT",
          message: "--what-if needs a client id but none was provided or configured.",
          hint: "Pass --client-id <appId>, or run from a host with ~/.m8t-stack/config.yaml.",
        });
      }
      const foundryResourceId = await resolveFoundryResourceId(foundryEndpoint, this.foundryResourceId);
      const repoRoot = await resolveRepoRoot();
      const acrPullIdentityResourceId = resolveAcrPullIdentity({ explicit: this.acrPullIdentity });
      const acrResourceId = acrPullIdentityResourceId ? "" : await resolveAcrResourceId({ imageRef: this.imageRef, explicit: this.acrResourceId });
      const params = buildBicepParams({
        location: this.location, suffix: this.suffix, imageRef: this.imageRef,
        tenantId: account.tenantId, clientId,
        foundryResourceId, foundryProjectEndpoint: foundryEndpoint,
        acrPullIdentityResourceId, acrResourceId,
        foundryTracingMode: parseFoundryTracingMode(this.foundryTracing),
        gatewayCpu: this.gatewayCpu,
        gatewayMemory: this.gatewayMemory,
        refereeEnabled: this.refereeEnabled,
        refereeBrainRepos: this.refereeBrainRepos,
        refereeFeedRepo: this.refereeFeedRepo,
        refereeInstallationId: this.refereeInstallationId,
        refereeWebhookHmacKvUri: this.refereeWebhookHmacKvUri,
        examKvUri: this.examKvUri,
        examLaWorkspaceId: this.examLaWorkspaceId,
        brainEvalDeployment: this.brainEvalDeployment,
        brainAppLogin: this.brainAppLogin,
        refereeCheckpointDir: this.refereeCheckpointDir,
        examApiBase: this.examApiBase,
      });
      const changes = await runWhatIf({ resourceGroup: this.resourceGroup, repoRoot, params });
      const classified = classifyWhatIf(changes);
      const { output, exitCode } = reportWhatIf(classified, { json: mode === "json" });
      this.context.stdout.write(output + "\n");
      return exitCode;
    }

    // 1. App reg (BYO or managed).
    log(this.clientId ? `using existing app reg ${this.clientId}` : "ensuring Entra app reg…");
    const app = await ensureAppReg({ tenantId: account.tenantId, clientId: this.clientId });
    if (app.appObjectId === null && this.clientId) {
      log(colors.dim("  (BYO app reg — skipping Graph writes; ensure it has http://localhost:3000 + the deployed FQDN as SPA redirect URIs and Expose-an-API)"));
    }

    // 2. Write config.yaml (Track 2 lib).
    await writeFoundryConfig({ tenantId: app.tenantId, clientId: app.clientId, projectEndpoint: foundryEndpoint });

    // 3. Resource group.
    log(`ensuring resource group ${this.resourceGroup}…`);
    await ensureResourceGroup(this.resourceGroup, this.location);

    // 4. Bicep.
    const foundryResourceId = await resolveFoundryResourceId(foundryEndpoint, this.foundryResourceId);
    const repoRoot = await resolveRepoRoot();
    const acrPullIdentityResourceId = resolveAcrPullIdentity({ explicit: this.acrPullIdentity });
    // Skip ACR resolution on the reference path — a pure-reference deploy must
    // not hit DEPLOY_ACR_NOT_FOUND from an unresolvable host label.
    const acrResourceId = acrPullIdentityResourceId
      ? ""
      : await resolveAcrResourceId({ imageRef: this.imageRef, explicit: this.acrResourceId });
    if (acrPullIdentityResourceId) {
      log(`using AcrPull identity ${acrPullIdentityResourceId.split("/").pop() ?? acrPullIdentityResourceId}`);
    } else if (acrResourceId) {
      log(`provisioning AcrPull identity for ${acrResourceId.split("/").pop() ?? acrResourceId}…`);
    }
    const params = buildBicepParams({
      location: this.location,
      suffix: this.suffix,
      imageRef: this.imageRef,
      tenantId: app.tenantId,
      clientId: app.clientId,
      foundryResourceId,
      foundryProjectEndpoint: foundryEndpoint,
      acrPullIdentityResourceId,
      acrResourceId,
      foundryTracingMode: parseFoundryTracingMode(this.foundryTracing),
      gatewayCpu: this.gatewayCpu,
      gatewayMemory: this.gatewayMemory,
      refereeEnabled: this.refereeEnabled,
      refereeBrainRepos: this.refereeBrainRepos,
      refereeFeedRepo: this.refereeFeedRepo,
      refereeInstallationId: this.refereeInstallationId,
      refereeWebhookHmacKvUri: this.refereeWebhookHmacKvUri,
      examKvUri: this.examKvUri,
      examLaWorkspaceId: this.examLaWorkspaceId,
      brainEvalDeployment: this.brainEvalDeployment,
      brainAppLogin: this.brainAppLogin,
      refereeCheckpointDir: this.refereeCheckpointDir,
      examApiBase: this.examApiBase,
    });
    log("running Bicep deployment (~3-5 min)…");
    const outputs = await runBicepDeployment({
      resourceGroup: this.resourceGroup,
      repoRoot,
      params,
      deploymentName: `m8t-deploy-${account.subscriptionId.slice(0, 8)}`,
    });

    // 5. Redirect URIs (managed only — BYO has no appObjectId).
    if (app.appObjectId) {
      await patchRedirectUris(app.appObjectId, outputs.containerAppFqdn);
    }

    const webappUrl = `https://${outputs.containerAppFqdn}`;
    if (mode === "json") {
      this.context.stdout.write(
        renderJson({ webapp: webappUrl, resourceGroup: this.resourceGroup, clientId: app.clientId, resources: outputs.resourceNames }) + "\n",
      );
      return 0;
    }
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "webapp", value: webappUrl },
        { key: "resource group", value: this.resourceGroup },
        { key: "client id", value: app.clientId },
        { key: "container app", value: outputs.resourceNames.containerApp ?? "-" },
      ]) + "\n",
    );
    this.context.stdout.write(colors.dim(`next: 'm8t open' to sign in to the webapp.\n`));
    return 0;
  }
}
